import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import PublicationTranslateButton from '../CopilotButtonAssistantUI/PublicationTranslateButton';
import PublicationCard from '../CopilotButtonAssistantUI/PublicationCard';
import { MessageTooltips } from '../MessagesTable/MessageTooltips';
import { ReactComponent as ArrowIcon } from '../CopilotButtonAssistantUI/assets/hide-arrows.svg';
import styles from './CopilotMinButton.module.scss';

const CLOSE_ANIMATION_MS = 300;
// Upper bound of publications rendered in the panel — evidence references can
// point at a case-wide pool of thousands, but the side panel shows the top of it.
const MAX_PUBLICATIONS = 50;
// narrative_story_id only accepts one value per request, so multi-story
// references fan out into parallel calls — bound that fan-out since the
// reference URL (and therefore the list length) is produced by the agent.
const MAX_STORY_REQUESTS = 10;

// href format (built by Copilot's `cite_publications` tool; `case` is the case the posts belong to):
// #ref-publications?count=12&publication_ids=1,2,3&story_ids=4&topic_ids=5,6&case=97085
const parseRefParams = (href) => {
  const params = new URLSearchParams(href?.split('?')[1] ?? '');
  const parseIds = (name) =>
    (params.get(name) ?? '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  return {
    count: Number(params.get('count')) || 0,
    publicationIds: parseIds('publication_ids'),
    storyIds: parseIds('story_ids'),
    topicIds: parseIds('topic_ids'),
  };
};

const PublicationsPanel = ({ href, narrativeId, onClose }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const { count } = parseRefParams(href);

  const nativeLanguage =
    currentUser?.translation_language ||
    window.clientInformation.language.split('-', 1)[0];

  const [data, setData] = useState(null);
  const [isBulkTranslated, setIsBulkTranslated] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setData(null);

    const { count, publicationIds, storyIds, topicIds } = parseRefParams(href);

    // The messages endpoint requires at least one primary selector.
    if (!narrativeId && publicationIds.length === 0 && storyIds.length === 0 && topicIds.length === 0) {
      setData([]);
      return;
    }

    const controller = new AbortController();

    const baseParams = () => {
      const params = new URLSearchParams();
      if (narrativeId) params.set('narrative_id', narrativeId);
      return params;
    };

    let requests;
    if (publicationIds.length > 0) {
      const params = baseParams();
      const ids = publicationIds.slice(0, MAX_PUBLICATIONS);
      ids.forEach((id) => params.append('ids', id));
      params.set('size', ids.length);
      requests = [params];
    } else if (storyIds.length > 0) {
      // narrative_story_id only accepts a single value — fetch per story and merge
      const cappedStoryIds = storyIds.slice(0, MAX_STORY_REQUESTS);
      // The merged result is trimmed to MAX_PUBLICATIONS, so shrink each
      // request accordingly (with some slack for cross-story duplicates).
      const perStorySize = Math.max(10, Math.ceil(MAX_PUBLICATIONS / cappedStoryIds.length));
      requests = cappedStoryIds.map((storyId) => {
        const params = baseParams();
        params.set('narrative_story_id', storyId);
        params.set('size', perStorySize);
        return params;
      });
    } else if (topicIds.length > 0) {
      const params = baseParams();
      topicIds.forEach((id) => params.append('topic_modeling_event_ids', id));
      params.set('size', MAX_PUBLICATIONS);
      requests = [params];
    } else {
      const params = baseParams();
      params.set('size', Math.min(count || MAX_PUBLICATIONS, MAX_PUBLICATIONS));
      requests = [params];
    }

    // API.fetch resolves {} for most non-OK statuses; the throwing onError
    // callback turns them into rejections so allSettled can tell a failed
    // request apart from a genuinely empty result.
    const rejectOnHttpError = (response) => {
      throw new Error(`Failed to load publications (HTTP ${response.status})`);
    };

    Promise.allSettled(
      requests.map((params) =>
        API.fetch('GET', `/API/v1/messages?${params.toString()}`, null, null, controller.signal, rejectOnHttpError),
      ),
    ).then((settled) => {
      if (controller.signal.aborted) return;
      const fulfilled = settled.filter((r) => r.status === 'fulfilled');
      // Keep partial results when only some story requests fail; surface an
      // error only when nothing at all came back.
      if (fulfilled.length === 0) {
        showRequestError(settled[0]?.reason, t('Publications'));
        setData([]);
        return;
      }
      const seen = new Set();
      const merged = [];
      fulfilled.forEach(({ value: result }) => {
        (result?.objects ?? []).forEach((message) => {
          if (!seen.has(message.id)) {
            seen.add(message.id);
            merged.push(message);
          }
        });
      });
      setData(merged.slice(0, MAX_PUBLICATIONS));
    });

    return () => controller.abort();
  }, [href, narrativeId, t]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  }, [onClose]);

  const handleBulkTranslate = useCallback((wantTranslation) => {
    setIsBulkTranslated(wantTranslation);
  }, []);


  return (
    <aside
      className={`${styles.publicationsPanel}${isClosing ? ` ${styles.publicationsPanelClosing}` : ''} copilot-publications-panel`}
      role="complementary"
      aria-label={t('Referenced publications')}
    >
      <div className={styles.panelTopRow}>
        <span className={styles.panelSectionLabel}>{t('Sources')}</span>
        <button
          type="button"
          className={styles.panelHideBtn}
          onClick={handleClose}
          aria-label={t('Hide publications panel')}
        >
          {t('Hide')}
          <ArrowIcon className={styles.panelHideArrow} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          {t('countPublications', { count: count || data?.length || 0 })}
        </h2>
        <div className={styles.panelHeaderActions}>
          {data && data.length > 0 && (
            <PublicationTranslateButton
              isTranslated={isBulkTranslated}
              isBulk={true}
              onToggle={handleBulkTranslate}
            />
          )}
        </div>
      </div>

      <MessageTooltips scope=".copilot-publications-panel" style={{ fontSize: '0.875rem' }} />

      <div className={styles.panelBody}>
        {data === null ? (
          <div className={styles.panelLoading} role="status" aria-live="polite">
            <span className={styles.panelLoadingText}>
              {t('Loading {{count}} publications…', { count })}
            </span>
          </div>
        ) : data.length === 0 ? (
          <p className={styles.panelEmpty}>{t('No publications found.')}</p>
        ) : (
          <div className={styles.publicationCardList}>
            {data.map((pub, i) => (
              <PublicationCard
                key={pub.id ?? i}
                message={pub}
                isBulkTranslated={isBulkTranslated}
                nativeLanguage={nativeLanguage}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default PublicationsPanel;
